// src/components/ImageZoomModal.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { FaChevronLeft, FaChevronRight, FaTrashAlt, FaSearchMinus } from "react-icons/fa";
import "../css/DynamicFormModal.css"

Modal.setAppElement("#root");

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP_PX = 8; // finger wobble that still counts as a tap, not a drag

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Keeps the panned image from being dragged past its own edges: at scale s the image is
// s× the stage, so it can travel at most half the overflow in each direction.
const clampOffset = (x, y, scale, rect) => {
  if (!rect || scale <= 1) return { x: 0, y: 0 };
  const maxX = ((scale - 1) * rect.width) / 2;
  const maxY = ((scale - 1) * rect.height) / 2;
  return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
};

// Shows one image at a time.
//
// Zoom: pinch with two fingers, double-tap, or (on a mouse) scroll wheel / double-click.
// Once zoomed, one finger — or the mouse — pans the image; the picture can't be dragged
// past its own edges. Tapping the image only closes the modal when it isn't zoomed, so a
// pan never dismisses what you're inspecting. Every caller gets this, form and app orders
// alike, because they all render this one component.
//
// When `images` (an array) has more than one entry, a prev/next control appears under the
// image and, if `onDelete` is supplied, a delete button appears at the bottom-right so
// extra images can be trimmed down to the one that should be kept. A single image (or the
// plain `imageUrl` prop) renders with neither control, unchanged from before.
const ImageZoomModal = ({
  isOpen,
  onRequestClose,
  imageUrl,
  images,
  currentIndex = 0,
  onNavigate,
  onDelete,
  deleting = false,
  alt = "Order Design",
}) => {
  const list = Array.isArray(images) && images.length > 0 ? images : (imageUrl ? [imageUrl] : []);
  const safeIndex = list.length > 0 ? Math.min(Math.max(currentIndex, 0), list.length - 1) : 0;
  const activeUrl = list[safeIndex];
  const hasMultiple = list.length > 1;

  const stageRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  // Live gesture bookkeeping. Kept in a ref so a move event never waits on a re-render.
  const gesture = useRef({
    mode: null,          // 'pan' | 'pinch' | null
    startScale: 1,
    startDist: 0,
    startMid: { x: 0, y: 0 },
    startOffset: { x: 0, y: 0 },
    startPoint: { x: 0, y: 0 },
    moved: false,
    lastTapAt: 0,
  });

  const viewRef = useRef(view);
  viewRef.current = view;

  const isZoomed = view.scale > 1.001;

  const resetView = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  // A different picture (or a fresh open) always starts at fit-to-screen.
  useEffect(() => { resetView(); }, [activeUrl, isOpen, resetView]);

  const stageRect = () => (stageRef.current ? stageRef.current.getBoundingClientRect() : null);

  // Zoom about a fixed screen point: the bit of the picture under the fingers (or cursor)
  // stays under them. Offsets are measured from the stage centre, matching the CSS transform.
  const zoomAround = useCallback((nextScale, clientX, clientY) => {
    const rect = stageRect();
    if (!rect) return;
    setView((prev) => {
      const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      if (scale === MIN_SCALE) return { scale, x: 0, y: 0 };
      const anchorX = clientX - (rect.left + rect.width / 2);
      const anchorY = clientY - (rect.top + rect.height / 2);
      const ratio = scale / prev.scale;
      const next = clampOffset(
        anchorX - (anchorX - prev.x) * ratio,
        anchorY - (anchorY - prev.y) * ratio,
        scale,
        rect
      );
      return { scale, x: next.x, y: next.y };
    });
  }, []);

  const toggleDoubleTapZoom = useCallback((clientX, clientY) => {
    if (isZoomed) resetView();
    else zoomAround(DOUBLE_TAP_SCALE, clientX, clientY);
  }, [isZoomed, resetView, zoomAround]);

  // ---- touch ---------------------------------------------------------------
  const onTouchStart = (e) => {
    const g = gesture.current;
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      g.mode = "pinch";
      g.moved = true; // a pinch is never a tap
      g.startScale = viewRef.current.scale;
      g.startDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
      g.startMid = { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
      g.startOffset = { x: viewRef.current.x, y: viewRef.current.y };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      g.mode = viewRef.current.scale > 1.001 ? "pan" : null;
      g.moved = false;
      g.startPoint = { x: t.clientX, y: t.clientY };
      g.startOffset = { x: viewRef.current.x, y: viewRef.current.y };
    }
  };

  const onTouchMove = (e) => {
    const g = gesture.current;
    const rect = stageRect();
    if (!rect) return;

    if (g.mode === "pinch" && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1;
      const mid = { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
      const scale = clamp((g.startScale * dist) / g.startDist, MIN_SCALE, MAX_SCALE);
      if (scale === MIN_SCALE) {
        setView({ scale, x: 0, y: 0 });
        return;
      }
      // Anchor on the pinch midpoint, and let that midpoint drag the picture too, so
      // pinching and moving the hand at the same time feels like one gesture.
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const m0 = { x: g.startMid.x - cx, y: g.startMid.y - cy };
      const m1 = { x: mid.x - cx, y: mid.y - cy };
      const ratio = scale / g.startScale;
      const next = clampOffset(
        m1.x - (m0.x - g.startOffset.x) * ratio,
        m1.y - (m0.y - g.startOffset.y) * ratio,
        scale,
        rect
      );
      setView({ scale, x: next.x, y: next.y });
      return;
    }

    if (g.mode === "pan" && e.touches.length === 1) {
      const t = e.touches[0];
      const dx = t.clientX - g.startPoint.x;
      const dy = t.clientY - g.startPoint.y;
      if (Math.abs(dx) > TAP_SLOP_PX || Math.abs(dy) > TAP_SLOP_PX) g.moved = true;
      setView((prev) => {
        const next = clampOffset(g.startOffset.x + dx, g.startOffset.y + dy, prev.scale, rect);
        return { ...prev, x: next.x, y: next.y };
      });
      return;
    }

    // Not zoomed and one finger down: remember that the finger travelled, so the
    // touch that follows isn't mistaken for a tap.
    if (e.touches.length === 1) {
      const t = e.touches[0];
      if (
        Math.abs(t.clientX - g.startPoint.x) > TAP_SLOP_PX ||
        Math.abs(t.clientY - g.startPoint.y) > TAP_SLOP_PX
      ) g.moved = true;
    }
  };

  const onTouchEnd = (e) => {
    const g = gesture.current;
    const wasPinch = g.mode === "pinch";
    const moved = g.moved;
    if (e.touches.length === 0) g.mode = null;
    else if (e.touches.length === 1 && wasPinch) {
      // Second finger lifted mid-pinch: carry on as a pan from where we are.
      const t = e.touches[0];
      g.mode = viewRef.current.scale > 1.001 ? "pan" : null;
      g.startPoint = { x: t.clientX, y: t.clientY };
      g.startOffset = { x: viewRef.current.x, y: viewRef.current.y };
      return;
    }
    if (wasPinch || moved) return;

    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const now = Date.now();
    if (now - g.lastTapAt < DOUBLE_TAP_MS) {
      g.lastTapAt = 0;
      e.preventDefault();       // a double tap zooms; it must not also fire a click
      toggleDoubleTapZoom(t.clientX, t.clientY);
    } else {
      g.lastTapAt = now;
    }
  };

  // ---- mouse / wheel -------------------------------------------------------
  useEffect(() => {
    const stage = stageRef.current;
    if (!isOpen || !stage) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAround(viewRef.current.scale * factor, e.clientX, e.clientY);
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [isOpen, activeUrl, zoomAround]);

  const onMouseDown = (e) => {
    if (!isZoomed || e.button !== 0) return;
    e.preventDefault();
    const g = gesture.current;
    g.mode = "pan";
    g.moved = false;
    g.startPoint = { x: e.clientX, y: e.clientY };
    g.startOffset = { x: viewRef.current.x, y: viewRef.current.y };
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onMove = (e) => {
      const g = gesture.current;
      if (g.mode !== "pan") return;
      const rect = stageRect();
      if (!rect) return;
      const dx = e.clientX - g.startPoint.x;
      const dy = e.clientY - g.startPoint.y;
      if (Math.abs(dx) > TAP_SLOP_PX || Math.abs(dy) > TAP_SLOP_PX) g.moved = true;
      setView((prev) => {
        const next = clampOffset(g.startOffset.x + dx, g.startOffset.y + dy, prev.scale, rect);
        return { ...prev, x: next.x, y: next.y };
      });
    };
    const onUp = () => {
      if (gesture.current.mode === "pan") gesture.current.mode = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isOpen]);

  // Tapping the picture still closes the modal — but only when it is not zoomed and the
  // pointer didn't travel, so panning or pinching never dismisses it mid-inspection.
  const onImageClick = (e) => {
    if (isZoomed || gesture.current.moved) {
      e.stopPropagation();
      gesture.current.moved = false;
      return;
    }
    onRequestClose();
  };

  if (list.length === 0 || !activeUrl) return null;

  const goPrev = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate((safeIndex - 1 + list.length) % list.length);
  };
  const goNext = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate((safeIndex + 1) % list.length);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="dfm-modal image-zoom-modal"
      overlayClassName="dfm-overlay image-zoom-overlay"
      closeTimeoutMS={200}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      <div className="dfm-header">
        <button className="dfm-close" onClick={onRequestClose}>
          ×
        </button>
      </div>

      <div className="image-zoom-body">
        {hasMultiple && (
          <span className="image-zoom-count">{safeIndex + 1} / {list.length}</span>
        )}

        <div
          className="image-zoom-stage"
          ref={stageRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          onMouseDown={onMouseDown}
          onDoubleClick={(e) => { e.preventDefault(); toggleDoubleTapZoom(e.clientX, e.clientY); }}
        >
          <img
            src={activeUrl}
            alt={alt}
            className="zoomed-image"
            draggable={false}
            onClick={onImageClick}
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
              cursor: isZoomed ? "grab" : "zoom-out",
            }}
          />
        </div>

        {isZoomed && (
          <button
            type="button"
            className="image-zoom-reset-btn"
            onClick={(e) => { e.stopPropagation(); resetView(); }}
            title="Reset zoom"
          >
            <FaSearchMinus />
            <span className="image-zoom-reset-label">{Math.round(view.scale * 100)}%</span>
          </button>
        )}

        {hasMultiple && (
          <div className="image-zoom-nav">
            <button
              type="button"
              className="image-zoom-nav-btn image-zoom-nav-prev"
              onClick={goPrev}
              title="Previous image"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="image-zoom-nav-btn image-zoom-nav-next"
              onClick={goNext}
              title="Next image"
            >
              <FaChevronRight />
            </button>
          </div>
        )}

        {hasMultiple && onDelete && (
          <button
            type="button"
            className="image-zoom-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!deleting) onDelete(safeIndex);
            }}
            disabled={deleting}
            title="Delete this image"
          >
            <FaTrashAlt />
          </button>
        )}
      </div>
    </Modal>
  );
};

export default ImageZoomModal;
